import { Component } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import {
  DetailList,
  DetailListItem,
  DetailListItemLink,
  DetailListItemButton,
  DetailListItemContent,
} from 'ui/Details';
import {
  Media,
  MediaImage,
  MediaBody,
  spacing,
  size,
  ButtonReset,
  Buttons,
  ButtonNew,
  Legend,
} from 'ui';
import { SidebarTitle } from 'ui/sidebar';
import Avatar from 'components/Avatar';
import ContactDetails from 'components/ContactDetails';
import { MdChevronLeft } from 'react-icons/md';
import { MdAdd } from 'react-icons/md';
import ContactSearchDropdownInput from 'components/ContactSearchDropdownInput';
import { MdClear } from 'react-icons/md';
import getAttachmentURL from 'utils/getAttachmentURL';
import { withTranslation } from 'react-i18next';
import { Stack } from '@tymate/margaret';
import { history } from 'store';
import { capitalize } from '@tymate/sergic';
// import SidebarSection from 'components/SidebarSection';

const ConversationNameWrapper = styled.div`
  display: flex-wrap;
  width: calc(100% + ${spacing(2)});
  margin: ${spacing(-1)} ${spacing(-1)} 0;
  padding: ${spacing()};
`;

const Input = styled.input`
  border: 1px solid ${({ theme }) => theme.separator};
  padding: ${spacing(0.25)};
  display: block;
  margin-left: ${spacing(-0.25)};
  width: 100%;
  outline: none;
`;

const CancelAddMemberButton = styled(ButtonReset)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 0;
`;

const BackLink = styled(Link)`
  color: inherit;
  display: block;
  width: calc(100% + ${spacing(2)});
  margin: ${spacing(-1)} ${spacing(-1)} 0;
  padding: ${spacing()};
  text-decoration: none;
  transition: background-color 150ms ease;
  font-weight: 500;
  border-bottom: 1px solid ${({ theme }) => theme.separatorLight};

  &:hover {
    background-color: rgba(0, 0, 0, 0.03);
  }
`;

const NewMembersSeparator = styled.li`
  font-size: ${size(-2)};
  text-transform: uppercase;
  background-color: ${({ theme }) => theme.separatorLight};
  padding: ${spacing(0.5)};
`;

const ContactInfos = styled.div`
  word-break: break-word;
`;

const ContactName = styled.p`
  margin: 0;
`;

const AddMemberMediaImage = styled(MediaImage)`
  padding: ${spacing(0.5)};

  svg {
    display: flex;
    align-items: center;
  }
`;

class ConversationDetails extends Component {
  state = {
    isAddingMembers: false,
    addedMembers: [],
    isEditingConversationName: false,
    conversationName: '',
  };

  componentDidMount() {
    this.setState({ conversationName: this.props.displayName || '' });
  }

  componentDidUpdate(prevProps) {
    const { displayName } = this.props;

    if (displayName !== prevProps.displayName) {
      this.setState({ conversationName: displayName });
    }
  }

  handleAddMember = member => {
    const { addedMembers } = this.state;

    this.setState({ addedMembers: [...addedMembers, member] });
  };

  handleActiveAddingMember = () => {
    this.setState({ isAddingMembers: true });
  };

  handleCancelAddMember = deletedMemberId => {
    const { addedMembers } = this.state;
    this.setState({
      addedMembers: addedMembers.filter(({ id }) => id !== deletedMemberId),
    });
  };

  handleSubmitAddMembers = async () => {
    const { addedMembers } = this.state;
    const { onAddMembers } = this.props;

    await onAddMembers(addedMembers);
    this.handleReset();
  };

  handleReset = () => {
    this.setState({ addedMembers: [], isAddingMembers: false });
  };

  handleSubmitConversationName = async () => {
    const { displayName, id, onUpdateName } = this.props;
    const { conversationName } = this.state;

    if (displayName === conversationName) {
      this.setState({ isEditingConversationName: false });
      return;
    }

    const conversation = {
      displayName: conversationName,
    };

    await onUpdateName(id, { conversation });
    this.setState({ isEditingConversationName: false });
    history.push(`/mes-echanges/mes-messages/${id}`);
  };

  render() {
    const { members, id, memberId, onArchive, t } = this.props;
    const { addedMembers, isAddingMembers, conversationName } = this.state;

    return (
      <>
        {Boolean(memberId) && (
          <Stack direction="column" size="full" gutterSize={1} alignX="center">
            <BackLink to={`/mes-echanges/mes-messages/${id}/details`}>
              <MdChevronLeft /> {t('misc:back')}
            </BackLink>
            <ContactDetails id={memberId} />
          </Stack>
        )}

        {!Boolean(memberId) && (
          <>
            <SidebarTitle hasNoHorizontalSpacing>
              {t('conversationDetails:name')}
            </SidebarTitle>
            <ConversationNameWrapper>
              <>
                <Input
                  value={conversationName}
                  onChange={e =>
                    this.setState({ conversationName: e.target.value })
                  }
                />
                <Buttons style={{ marginTop: spacing(1) }}>
                  <ButtonNew onClick={this.handleSubmitConversationName}>
                    {t('conversationDetails:modify')}
                  </ButtonNew>
                </Buttons>
              </>
            </ConversationNameWrapper>

            <SidebarTitle hasNoHorizontalSpacing>
              {t('conversationDetails:members')}
            </SidebarTitle>
            <DetailList stacked bare>
              {members.filter(Boolean).map(member => (
                <DetailListItem key={member.id} tight>
                  <DetailListItemLink
                    to={`/mes-echanges/mes-messages/${id}/details/${member.id}`}
                  >
                    <Media>
                      <MediaImage>
                        <Avatar
                          {...member}
                          avatarURL={
                            Boolean(member.avatarUrl)
                              ? getAttachmentURL(member.avatarUrl)
                              : undefined
                          }
                          small
                        />
                      </MediaImage>
                      <MediaBody>
                        <ContactInfos>
                          <ContactName>
                            {capitalize(member.displayName)}
                          </ContactName>
                          <Legend style={{ margin: 0 }}>
                            {t(`jobslang:${member.displayPlaceRole}`)}
                          </Legend>
                        </ContactInfos>
                      </MediaBody>
                    </Media>
                  </DetailListItemLink>
                </DetailListItem>
              ))}

              {(addedMembers.length > 0 || isAddingMembers) && (
                <NewMembersSeparator>
                  {t('conversationDetails:newMembers')} ↓
                </NewMembersSeparator>
              )}

              {addedMembers.map((member, index) => (
                <DetailListItem
                  key={member.id}
                  tight
                  isFirstOfModified={index === 0}
                >
                  <DetailListItemContent>
                    <Media>
                      <MediaImage>
                        <Avatar small {...member} />
                      </MediaImage>
                      <MediaBody>
                        <CancelAddMemberButton
                          onClick={() => this.handleCancelAddMember(member.id)}
                        >
                          <MdClear />
                        </CancelAddMemberButton>
                        <ContactInfos>
                          <ContactName>
                            {capitalize(member.displayName)}
                          </ContactName>
                        </ContactInfos>
                      </MediaBody>
                    </Media>
                  </DetailListItemContent>
                </DetailListItem>
              ))}

              {isAddingMembers && (
                <DetailListItem tight>
                  <DetailListItemContent>
                    <Media>
                      <MediaImage>
                        <Avatar small />
                      </MediaImage>
                      <MediaBody>
                        <ContactSearchDropdownInput
                          focusOnMount
                          members={members.concat(addedMembers)}
                          onChange={this.handleAddMember}
                        />
                      </MediaBody>
                    </Media>
                  </DetailListItemContent>
                </DetailListItem>
              )}

              {!isAddingMembers && (
                <DetailListItem tight>
                  <DetailListItemButton onClick={this.handleActiveAddingMember}>
                    <Stack alignY="center">
                      <AddMemberMediaImage>
                        <MdAdd />
                      </AddMemberMediaImage>
                      <MediaBody>{t('conversationDetails:add')}</MediaBody>
                    </Stack>
                  </DetailListItemButton>
                </DetailListItem>
              )}
            </DetailList>

            {isAddingMembers && (
              <Buttons style={{ marginTop: spacing(-1.5) }}>
                <ButtonNew
                  isPrimary
                  disabled={addedMembers.length === 0}
                  onClick={this.handleSubmitAddMembers}
                >
                  {t('misc:save')}
                </ButtonNew>
                <ButtonNew onClick={this.handleReset}>
                  {t('misc:cancel')}
                </ButtonNew>
              </Buttons>
            )}

            <ButtonNew isPrimary onClick={onArchive}>
              {t('conversationDetails:archive')}
            </ButtonNew>
          </>
        )}
      </>
    );
  }
}

export default withTranslation()(ConversationDetails);
